import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [userSettings, setUserSettings] = useState({
    theme: 'light',
    notifications: true,
    studyGoals: {
      dailyQuestions: 10,
      weeklyPTs: 1,
    },
  });
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const loadUserSettings = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        // Try to load from cache first
        const cachedSettings = localStorage.getItem(`userSettings_${currentUser.uid}`);
        if (cachedSettings) {
          setUserSettings(JSON.parse(cachedSettings));
        }

        // If online, try to fetch from Firestore
        if (isOnline) {
          const userDocRef = doc(db, 'userSettings', currentUser.uid);
          const docSnap = await getDoc(userDocRef);

          if (docSnap.exists()) {
            const firestoreSettings = docSnap.data();
            setUserSettings(firestoreSettings);
            localStorage.setItem(`userSettings_${currentUser.uid}`, JSON.stringify(firestoreSettings));
          } else {
            // Initialize default settings for new users
            await setDoc(userDocRef, userSettings);
            localStorage.setItem(`userSettings_${currentUser.uid}`, JSON.stringify(userSettings));
          }
        }
      } catch (error) {
        console.error('Error loading user settings:', error);
        // Already using cached settings if available
      } finally {
        setLoading(false);
      }
    };

    loadUserSettings();
  }, [currentUser, isOnline]);

  const updateUserSettings = async (newSettings) => {
    try {
      if (!currentUser) {
        throw new Error('User must be logged in to update settings');
      }

      // Update local state and cache immediately
      setUserSettings(newSettings);
      localStorage.setItem(`userSettings_${currentUser.uid}`, JSON.stringify(newSettings));

      // If online, update Firestore
      if (isOnline) {
        const userDocRef = doc(db, 'userSettings', currentUser.uid);
        await setDoc(userDocRef, newSettings);
      } else {
        // Queue update for when back online
        const pendingUpdates = JSON.parse(localStorage.getItem('pendingSettingsUpdates') || '[]');
        pendingUpdates.push({
          userId: currentUser.uid,
          settings: newSettings,
          timestamp: new Date().toISOString()
        });
        localStorage.setItem('pendingSettingsUpdates', JSON.stringify(pendingUpdates));
      }
    } catch (error) {
      console.error('Error updating user settings:', error);
      throw error;
    }
  };

  // Process pending updates when coming back online
  useEffect(() => {
    const processPendingUpdates = async () => {
      if (!isOnline || !currentUser) return;

      const pendingUpdates = JSON.parse(localStorage.getItem('pendingSettingsUpdates') || '[]');
      if (pendingUpdates.length === 0) return;

      const userUpdates = pendingUpdates.filter(update => update.userId === currentUser.uid);
      if (userUpdates.length === 0) return;

      try {
        // Apply the most recent update
        const mostRecent = userUpdates[userUpdates.length - 1];
        const userDocRef = doc(db, 'userSettings', currentUser.uid);
        await setDoc(userDocRef, mostRecent.settings);

        // Clear processed updates
        const remainingUpdates = pendingUpdates.filter(update => update.userId !== currentUser.uid);
        localStorage.setItem('pendingSettingsUpdates', JSON.stringify(remainingUpdates));
      } catch (error) {
        console.error('Error processing pending updates:', error);
      }
    };

    processPendingUpdates();
  }, [isOnline, currentUser]);

  const value = {
    userSettings,
    updateUserSettings,
    loading,
    isOnline
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider; 